/**
 * „Jetzt buchen, später zahlen“ bewerben (Kevin 30.10.2026: mehr Abschlüsse).
 * Zeigt sich nur, solange die Zahlungsart `pay_later` im Admin aktiv ist (GET /payment-methods, einmal je Seitenaufruf).
 * variant="pill": kurzer Hinweis im Seitenkopf · variant="card": Erklärung in drei Schritten.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarCheck, ArrowRight } from "lucide-react";
import client from "@/lib/api";
import { liveEvent } from "@/lib/liveEvent";

let request = null;
function usePayLater() {
  const [cfg, setCfg] = useState(null);
  useEffect(() => {
    if (!request) {
      request = client.get("/payment-methods")
        .then((r) => (r.data?.methods || []).find((m) => m.id === "pay_later" && m.enabled) || null)
        .catch(() => null);
    }
    let alive = true;
    request.then((v) => { if (alive) setCfg(v); });
    return () => { alive = false; };
  }, []);
  return cfg;
}

export default function PayLaterPromo({ variant = "pill", lang = "de", className = "" }) {
  const cfg = usePayLater();
  if (!cfg) return null;
  const days = Number(cfg.due_days || 7);
  const en = lang === "en";
  if (variant === "pill") {
    return (
      <Link
        to="/buchen"
        onClick={() => liveEvent("Später-zahlen-Hinweis angeklickt")}
        className={`group inline-flex items-center gap-2.5 border border-gold/60 bg-gold/[0.08] px-4 py-2 text-[0.78rem] text-cream hover:bg-gold/[0.14] transition-colors ${className}`}
        data-testid="pay-later-promo"
      >
        <span className="bg-gold-fill text-ink-950 text-[0.6rem] font-bold uppercase tracking-[0.2em] px-2 py-0.5">{en ? "New" : "Neu"}</span>
        <span>
          {en ? <>Book now, <strong className="text-gold">pay {days} days before your party</strong></>
              : <>Jetzt buchen, <strong className="text-gold">erst {days} Tage vor der Feier zahlen</strong></>}
        </span>
        <ArrowRight className="h-3.5 w-3.5 text-gold transition-transform group-hover:translate-x-0.5" />
      </Link>
    );
  }
  const steps = [
    ["Wunschtermin sichern", "Sie buchen online in wenigen Minuten, Ihr Termin ist sofort verbindlich reserviert."],
    ["Heute 0 € zahlen", "Keine Anzahlung, keine Kaution. Sie bekommen Rechnung und Bankdaten per E-Mail."],
    [`${days} Tage vor der Feier überweisen`, "Wir erinnern Sie rechtzeitig per E-Mail. Danach müssen Sie sich um nichts mehr kümmern."],
  ];
  return (
    <section className={`py-14 sm:py-16 border-t border-cream/[0.06] ${className}`} data-testid="pay-later-card">
      <div className="container-x">
        <div className="relative border-2 border-gold/60 bg-gold/[0.05] p-6 sm:p-10 overflow-hidden">
          <span className="absolute top-0 right-0 bg-gold-fill text-ink-950 text-[0.62rem] uppercase tracking-[0.22em] font-bold px-3 py-1.5">Neu</span>
          <p className="eyebrow mb-3"><span className="gold-line" /> Jetzt buchen, später zahlen</p>
          <h2 className="font-display text-3xl sm:text-4xl leading-tight">
            Termin heute sichern, <span className="italic text-gold">erst {days} Tage vor der Feier zahlen.</span>
          </h2>
          <div className="mt-8 grid gap-5 sm:grid-cols-3">
            {steps.map(([t, d], i) => (
              <div key={t} className="flex gap-4">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center border border-gold/60 font-display text-gold">{i + 1}</span>
                <div>
                  <p className="font-semibold text-cream">{t}</p>
                  <p className="mt-1 text-sm text-cream/70 leading-relaxed">{d}</p>
                </div>
              </div>
            ))}
          </div>
          <Link to="/buchen" className="mt-8 inline-flex items-center gap-3 bg-gold-fill text-ink-950 px-6 py-3 text-[0.75rem] font-semibold uppercase tracking-[0.2em]">
            <CalendarCheck className="h-4 w-4" /> Wunschtermin prüfen
          </Link>
        </div>
      </div>
    </section>
  );
}
